import { Drawer, DrawerContextProvider, DrawerMode, Flex, notify } from '@billy/design-system'
import { Button } from '@shinetools/lumen-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useCreateContact } from '@features/contacts/hooks/useCreateContact'
import { useUpdateContact } from '@features/contacts/hooks/useUpdateContact'
import { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../../enums/queryKeys'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useForm } from '../../../../../../../../hooks/useForm'
import { useSegment } from '../../../../../../../../hooks/useSegment'
import { APIResponse } from '../../../../../../../../utils'
import { ManageSupplier } from './elements/ManageSupplier'
import type { SupplierFormValues } from './types/supplierForm'
import { getCreateSupplierContactPayload } from './utils/getCreateSupplierContactPayload'
import { getUpdateSupplierContactPayload } from './utils/getUpdateSupplierContactPayload'
import { getSupplierContactType, getSupplierFormDefaultValues, getSupplierFormSchema } from './utils/supplierForm'

export type SupplierDrawerMode = 'create' | 'edit'

interface SupplierDrawerProps {
  contact: Contact | null
  isOpen: boolean
  mode?: SupplierDrawerMode
  onClose?: () => void
  title: string
}

export const SupplierDrawer = ({ contact, isOpen, mode = 'edit', onClose, title }: SupplierDrawerProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()
  const { track } = useSegment()
  const drawerMode = isOpen ? DrawerMode.Opened : DrawerMode.Closed
  const isCreateMode = mode === 'create'
  // Country is optional for companies, so without this a supplier created from the list can end up
  // without one - and the DK purchase category in the automation section never shows up for it.
  // Editing an existing contact must not inherit this: it's passed as undefined there, so a saved
  // contact with no country stays that way.
  const defaultCountryId = isCreateMode ? organization?.countryId : undefined
  const trackingContext = isCreateMode ? TrackingContext.CreateContact : TrackingContext.EditContact

  // The schema already validates these fields client-side, so any error the API reports is unexpected
  // rather than something the user can fix on a specific field - a toast covers it.
  const handleUpdateError = () => {
    notify({
      id: NotificationKeys.UpdateContact,
      message: t('suppliers.detail.drawer.notification.update_error'),
      variant: 'error',
    })
  }

  const handleCreateError = () => {
    notify({
      id: NotificationKeys.CreateContact,
      message: t('suppliers.detail.form.create.notification.error'),
      variant: 'error',
    })
  }

  const handleUpdateSuccess = useCallback(
    async (data: APIResponse<Contact>) => {
      const updatedContact = data.contacts[0]

      await queryClient.invalidateQueries([QueryKeys.Contact, updatedContact.id])
      await queryClient.invalidateQueries(QueryKeys.Contacts)

      notify({
        id: NotificationKeys.UpdateContact,
        message: t('suppliers.detail.drawer.notification.update_success'),
        variant: 'success',
      })
      onClose?.()
    },
    [onClose, queryClient, t],
  )

  const handleCreateSuccess = useCallback(async () => {
    await queryClient.invalidateQueries(QueryKeys.Contacts)
    await queryClient.invalidateQueries(QueryKeys.ContactsAndContactPersons)

    notify({
      id: NotificationKeys.CreateContact,
      message: t('suppliers.detail.form.create.notification.success'),
      variant: 'success',
    })
    onClose?.()
    track('Contact Created (Client)')
  }, [onClose, queryClient, t, track])

  const { isProcessing: isCreating, create: createSupplierContact } = useCreateContact({
    onError: handleCreateError,
    onSuccess: handleCreateSuccess,
  })

  const { isProcessing: isUpdating, update: updateSupplierContact } = useUpdateContact({
    onError: handleUpdateError,
    onSuccess: handleUpdateSuccess,
  })

  const { Form, reset, submitHandler, watch } = useForm({
    defaultValues: useMemo(
      () => getSupplierFormDefaultValues(contact, getSupplierContactType(contact), defaultCountryId),
      [contact, defaultCountryId],
    ),
    onSubmit: (values: SupplierFormValues) => {
      if (isCreateMode) {
        if (!organization?.id) {
          return
        }

        createSupplierContact(getCreateSupplierContactPayload({ organizationId: organization.id, values }))
        return
      }

      const payload = getUpdateSupplierContactPayload(values, contact)

      if (!contact?.id || Object.keys(payload).length === 0) {
        return
      }

      updateSupplierContact({ ...payload, id: contact.id })
    },
    validationSchema: useMemo(() => getSupplierFormSchema(t), [t]),
  })

  const formValues = watch()
  const hasChanges = Object.keys(getUpdateSupplierContactPayload(formValues, contact)).length > 0
  const isSaveDisabled = isCreateMode ? !organization?.id : !contact?.id || !hasChanges

  // The drawer stays mounted while the supplier loads and after a save, so the fields follow the saved contact.
  // Closing a create drawer also clears whatever was typed, so the next open starts empty.
  useEffect(() => {
    reset(getSupplierFormDefaultValues(contact, getSupplierContactType(contact), defaultCountryId))
  }, [contact, defaultCountryId, isOpen, reset])

  const handleCloseRequest = () => {
    reset(getSupplierFormDefaultValues(contact, getSupplierContactType(contact), defaultCountryId))
    onClose?.()
  }

  return (
    <DrawerContextProvider drawerMode={drawerMode} onCloseRequest={handleCloseRequest}>
      <Drawer closeOnClickAway={false}>
        <Drawer.Header title={title} />
        <Drawer.Body>
          <Form>
            <ManageSupplier contact={contact} trackingContext={trackingContext} />
          </Form>
        </Drawer.Body>
        <Drawer.Footer>
          <Flex justifyContent="flex-end" width="100%">
            <Button
              disabled={isSaveDisabled}
              label={t(
                isCreateMode ? 'customer_modal.footer.button.create.label' : 'suppliers.detail.drawer.save_changes',
              )}
              loading={isCreating || isUpdating}
              onClick={submitHandler}
            />
          </Flex>
        </Drawer.Footer>
      </Drawer>
    </DrawerContextProvider>
  )
}
